// Team dashboard view (/dashboard): read-only team overview.
// Strings localized via i18n (English default, RU parity). Pure render; navigation injected.

import type { DashboardMember } from '../dashboard.js';
import { t } from './i18n.js';

interface DashboardViewOpts {
  data: {
    team: { id: string; name: string } | null;
    role: string | null;
    joinCode: string | null;
    members: DashboardMember[];
  };
  navigate: (path: string) => void;
}

function roleLabel(role: string | null): string { return role === 'owner' ? t('dashboard.roleOwner') : t('dashboard.roleMember'); }
function initial(email: string | null): string { return (email ?? '?').charAt(0).toUpperCase(); }

export function mountDashboardView(host: HTMLElement, opts: DashboardViewOpts): void {
  host.innerHTML = '';
  const root = document.createElement('div');
  root.className = 'dashboard-view';

  const back = document.createElement('button');
  back.dataset.role = 'back';
  back.className = 'dash-back';
  back.textContent = t('dashboard.back');
  back.addEventListener('click', () => opts.navigate('/library'));
  root.appendChild(back);

  const { team, role, joinCode, members } = opts.data;

  if (!team) {
    const empty = document.createElement('p');
    empty.dataset.role = 'empty';
    empty.className = 'dash-empty';
    empty.textContent = t('dashboard.empty');
    root.appendChild(empty);
    host.appendChild(root);
    return;
  }

  const banner = document.createElement('section');
  banner.className = 'dash-banner';

  const left = document.createElement('div');
  const h = document.createElement('h1');
  h.className = 'dash-team';
  h.textContent = team.name;
  left.appendChild(h);
  const selfBadge = document.createElement('span');
  selfBadge.dataset.role = 'self-role';
  selfBadge.className = `dash-badge dash-badge-${role === 'owner' ? 'owner' : 'member'}`;
  selfBadge.textContent = roleLabel(role);
  left.appendChild(selfBadge);
  banner.appendChild(left);

  if (joinCode) {
    const codeWrap = document.createElement('div');
    codeWrap.dataset.role = 'join-code';
    codeWrap.className = 'dash-code';
    const lbl = document.createElement('div');
    lbl.className = 'dash-code-label';
    lbl.textContent = t('dashboard.joinCode');
    const codeRow = document.createElement('div');
    codeRow.className = 'dash-code-row';
    const code = document.createElement('code');
    code.textContent = joinCode;
    const copy = document.createElement('button');
    copy.dataset.role = 'copy-code';
    copy.className = 'dash-copy';
    copy.textContent = t('dashboard.copy');
    copy.addEventListener('click', () => {
      try { void navigator.clipboard?.writeText(joinCode); } catch { /* clipboard unavailable */ }
      copy.textContent = t('dashboard.copied');
      setTimeout(() => { copy.textContent = t('dashboard.copy'); }, 1500);
    });
    codeRow.appendChild(code);
    codeRow.appendChild(copy);
    codeWrap.appendChild(lbl);
    codeWrap.appendChild(codeRow);
    banner.appendChild(codeWrap);
  }
  root.appendChild(banner);

  const list = document.createElement('section');
  list.className = 'dash-members';
  const mhead = document.createElement('div');
  mhead.dataset.role = 'members-head';
  mhead.className = 'dash-mhead';
  mhead.textContent = t('dashboard.members', { n: members.length });
  list.appendChild(mhead);
  for (const m of members) {
    const row = document.createElement('div');
    row.dataset.role = 'member-row';
    row.className = 'dash-row';
    const av = document.createElement('div');
    av.className = 'dash-av';
    av.textContent = initial(m.email);
    const email = document.createElement('div');
    email.className = 'dash-email';
    email.textContent = m.email ?? t('dashboard.unknownEmail');
    const rbadge = document.createElement('span');
    rbadge.className = `dash-badge dash-badge-${m.role === 'owner' ? 'owner' : 'member'}`;
    rbadge.textContent = roleLabel(m.role);
    row.appendChild(av);
    row.appendChild(email);
    row.appendChild(rbadge);
    list.appendChild(row);
  }
  root.appendChild(list);

  host.appendChild(root);
}
